import { selectTextOptions } from "@/store/canvas/canvasSelectors";
import { setTextOptions } from "@/store/canvas/canvasSlice";
import { useDispatch, useSelector } from "react-redux";
import { TextInspectorContent } from "./TextInspectorContent";
import { TextOptions } from "@/components/canvas/types/textTypes";
import { FontFamily } from "@/components/canvas/types/canvasTypes";
import { useDispatchChangeTextOptionsEvent } from "@/components/canvas/hooks/event/useTextChangeOptionsEvent";
import { useDispatchDeleteActiveObjectEvent } from "@/components/canvas/hooks/event/useDeleteActiveObjectEvent";
import { useDispatchChangeLayerEvent } from "@/components/canvas/hooks/event/useChangeLayerEvent";

export type TextDecoration = "bold" | "italic" | "underline" | "striked";

export const TextInspector = () => {
  const dispatch = useDispatch();
  const textOptions = useSelector(selectTextOptions);
  const dispatchChangeTextOptions = useDispatchChangeTextOptionsEvent();
  const dispatchDeleteActiveObject = useDispatchDeleteActiveObjectEvent();
  const dispatchChangeLayer = useDispatchChangeLayerEvent();

  if (!textOptions) {
    return null;
  }

  const updateTextOptions = (options: typeof textOptions) => {
    dispatchChangeTextOptions(options);
    dispatch(setTextOptions(options));
  };

  const createOptionChangeHandler =
    <T extends keyof NonNullable<typeof textOptions>>(key: T) =>
    (value: NonNullable<typeof textOptions>[T]) => {
      if (!textOptions) return;

      const updatedOptions = { ...textOptions, [key]: value };
      updateTextOptions(updatedOptions);
    };

  const handleTextDecorationChange = (values: TextDecoration[]) => {
    const newTextOptions = getUpdatedTextOptionsWithTextDecorations(textOptions, values);

    updateTextOptions(newTextOptions);
  };

  return (
    <TextInspectorContent
      textAlignOption={{
        value: textOptions.textAlign,
        onChange: createOptionChangeHandler("textAlign"),
      }}
      rotationOption={{
        value: textOptions.angle,
        onChange: createOptionChangeHandler("angle"),
      }}
      widthOption={{
        value: textOptions.width,
        onChange: createOptionChangeHandler("width"),
      }}
      xPositionOption={{
        value: textOptions.left,
        onChange: createOptionChangeHandler("left"),
      }}
      yPositionOption={{
        value: textOptions.top,
        onChange: createOptionChangeHandler("top"),
      }}
      strokeColorOption={{
        value: textOptions.stroke,
        onChange: createOptionChangeHandler("stroke"),
      }}
      strokeWidthOption={{
        value: textOptions.strokeWidth,
        onChange: createOptionChangeHandler("strokeWidth"),
      }}
      fontSizeOption={{
        value: textOptions.fontSize,
        onChange: createOptionChangeHandler("fontSize"),
      }}
      fontFamilyOption={{
        value: textOptions.fontFamily as FontFamily,
        onChange: createOptionChangeHandler("fontFamily"),
      }}
      textDecorationsOption={{
        value: getTextDecorationsFromTextOptions(textOptions),
        onChange: handleTextDecorationChange,
      }}
      fillOption={{
        value: textOptions.fill,
        onChange: createOptionChangeHandler("fill"),
      }}
      opacityOption={{
        value: textOptions.opacity,
        onChange: createOptionChangeHandler("opacity"),
      }}
      // @ts-ignore
      handleLayerChange={dispatchChangeLayer}
      onDeleteElementClick={() => {
        dispatchDeleteActiveObject(undefined);
      }}
    />
  );
};

function getUpdatedTextOptionsWithTextDecorations(
  textOptions: NonNullable<TextOptions>,
  decorations: TextDecoration[]
): NonNullable<TextOptions> {
  const newTextOptions = { ...textOptions };
  newTextOptions.fontWeight = decorations.includes("bold") ? "bold" : "normal";
  newTextOptions.fontStyle = decorations.includes("italic") ? "italic" : "normal";
  newTextOptions.underline = decorations.includes("underline");
  newTextOptions.linethrough = decorations.includes("striked");

  return newTextOptions;
}

function getTextDecorationsFromTextOptions(textOptions: NonNullable<TextOptions>) {
  const decorations: TextDecoration[] = [];

  if (textOptions.fontWeight === "bold") {
    decorations.push("bold");
  }
  if (textOptions.fontStyle === "italic") {
    decorations.push("italic");
  }
  if (textOptions.underline) {
    decorations.push("underline");
  }
  if (textOptions.linethrough) {
    decorations.push("striked");
  }

  return decorations;
}
